/**
 * Here there be dragons.
 * Denne fila finnes fordi når vi brukte draft.js, lagde vi parsinglogikken i mittnte-frontend spesifikt for det formatet.
 * Så når vi i april 2026 byttet fra draftjs til lexical, tok vi et valg om å heller konvertere til det gamle formatet, for å slippe å oppdatere mittnte-frontend,
 * og samtidig slippe å konverte det lagrede innholdet i databasen. Mulig vi heller burde ha skrevet om hele greia, men det er årsaken til denne monstrositeten.
 */
import { $getRoot } from "lexical";

import { $createImageNode } from "components/ContentEditor/ImageNode";
import { createHeadingBlock, createListBlock, createParagraphBlock } from "components/ContentEditor/converter/blockHelpers";
import { draftJSToLexical } from "components/ContentEditor/converter/draftJSToLexical";
import { lexicalToDraftJS } from "components/ContentEditor/converter/lexicalToDraftJS";
import { LexicalFormat, LexicalNode } from "components/ContentEditor/converter/types";

/**
 * Detect if the content is in draft-js or Lexical format
 */
export function detectFormat(content: string): "draft-js" | "lexical" | "unknown" {
	try {
		const parsed = JSON.parse(content);
		if (Array.isArray(parsed) || (parsed.blocks && Array.isArray(parsed.blocks))) {
			return "draft-js";
		} else if (parsed.root) {
			return "lexical";
		}
		return "unknown";
	} catch {
		return "unknown";
	}
}

/**
 * Convert any format to the target format
 */
export function convertContent(content: string, targetFormat: "draft-js" | "lexical"): string {
	const currentFormat = detectFormat(content);

	if (currentFormat === "unknown") {
		console.warn("Unknown content format, returning as-is");
		return content;
	}

	if (currentFormat === targetFormat) {
		return content;
	}

	try {
		if (currentFormat === "draft-js" && targetFormat === "lexical") {
			const lexical = draftJSToLexical(content);
			return JSON.stringify(lexical);
		} else if (currentFormat === "lexical" && targetFormat === "draft-js") {
			const draftjs = lexicalToDraftJS(content);
			return JSON.stringify(draftjs);
		}
	} catch (e) {
		console.error("Error converting content:", e);
	}

	return content;
}

/**
 * Load content into Lexical editor from either draft-js or Lexical format
 * Returns a function that applies the state to the editor
 */
export function loadContentIntoEditor(content: string) {
	try {
		const format = detectFormat(content);
		let lexicalJson: LexicalFormat | undefined;

		if (format === "draft-js") {
			lexicalJson = draftJSToLexical(content);
		} else if (format === "lexical") {
			lexicalJson = JSON.parse(content);
		} else {
			return;
		}

		return () => {
			const root = $getRoot();
			root.clear();

			let currentList: ReturnType<typeof createListBlock> | null = null;
			let currentListType: string | null = null;

			lexicalJson!.root.children.forEach((block: LexicalNode) => {
				try {
					if (block.type === "image" && block.src) {
						currentList = null;
						currentListType = null;
						const imageNode = $createImageNode({
							src: block.src as string,
							altText: (block.altText as string) || "",
							width: block.width as number,
							height: block.height as number,
						});
						root.append(imageNode);
					} else if (block.type === "heading") {
						currentList = null;
						currentListType = null;
						const headingNode = createHeadingBlock(block);
						root.append(headingNode);
					} else if (block.type === "list") {
						// If we're starting a new list type, replace current list
						if (!currentList || currentListType !== block.listType) {
							currentList = createListBlock(block, (list) => {
								root.append(list);
							});
							currentListType = block.listType as string;
						} else {
							// Add to existing list
							createListBlock(block, (list) => {
								list.getChildren().forEach((item) => {
									currentList?.append(item);
								});
							});
						}
					} else {
						currentList = null;
						currentListType = null;
						const paragraphNode = createParagraphBlock(block);
						root.append(paragraphNode);
					}
				} catch (blockErr) {
					console.error("Error processing block:", blockErr);
				}
			});
		};
	} catch (e) {
		console.error("Error loading content:", e);
	}
}

// Re-export types for convenience
export type { DraftJSFormat, Entity, EntityRange, InlineStyleRange, LexicalFormat, LexicalNode } from "components/ContentEditor/converter/types";

// Re-export conversion functions
export { draftJSToLexical, lexicalToDraftJS };
